import type {
    ApiArticle,
    ApiBulkMaterialUnit,
    ApiCustomerTrailerUnit,
    ApiGoods,
    ApiHandlingUnit,
    ApiSwapBodyUnit,
} from '#api/tourservice/model/types.ts';
import type {
    Article,
    ArticleType,
    BulkMaterialQuantity,
    BulkMaterialUnit,
    CustomerTrailerUnit,
    Goods,
    HandlingUnit,
    SwapBodyUnit,
} from '#domain/model/Goods.ts';
import { type CargoPackagingTypes, cargoPackagingTranslationKeys } from '#domain/model/packagingOptions.ts';

const articleTypes = [
    'piece',
    'leaf',
    'drum',
    'pair',
    'roll',
    'set',
    'meter',
    'square-meter',
    'cubic-meter',
    'kilogram',
] as const satisfies readonly ArticleType[];

const bulkMaterialQuantityTypes = [
    'kilogram',
    'ton',
    'liter',
    'cubic-meter',
] as const satisfies readonly BulkMaterialQuantity['type'][];

const isOneOf = <T extends string>(value: string, values: readonly T[]): value is T => values.includes(value as T);

const mapPackagingType = (type: string): CargoPackagingTypes =>
    type in cargoPackagingTranslationKeys ? (type as CargoPackagingTypes) : 'unspecified';

const mapArticleQuantity = (quantity: ApiArticle['quantity']): Article['quantity'] => {
    if (!quantity || !isOneOf(quantity.type, articleTypes)) {
        return;
    }

    return {
        type: quantity.type,
        value: quantity.value,
    };
};

const mapBulkMaterialQuantity = (quantity: ApiBulkMaterialUnit['quantity']): BulkMaterialQuantity => {
    if (!isOneOf(quantity.type, bulkMaterialQuantityTypes)) {
        return {
            type: 'kilogram',
            value: quantity.value,
        };
    }

    return {
        type: quantity.type,
        value: quantity.value,
    };
};

export const mapGoods = (goods: ApiGoods): Goods | undefined => {
    switch (goods.type) {
        case 'cargo-material':
            return {
                type: 'cargo-material',
                units: goods.handling_units.map(mapHandlingUnit),
            };
        case 'bulk-material':
            return {
                type: 'bulk-material',
                units: goods.units.map(mapBulkMaterialUnit),
            };
        case 'swap-body':
            return {
                type: 'swap-body',
                units: goods.units.map(mapSwapBodyUnit),
            };
        case 'customer-trailer':
            return {
                type: 'customer-trailer',
                units: goods.trailers.map(mapCustomerTrailer),
            };
    }
};

const mapHandlingUnit = (unit: ApiHandlingUnit): HandlingUnit => ({
    id: unit.id,
    type: 'cargo-material',
    articles: unit.articles?.map(mapArticle) ?? [],
    packageType: mapPackagingType(unit.packaging_type),
    quantity: {
        target: unit.quantity,
        loaded: unit.proof_of_loading ? (unit.proof_of_loading?.deviation?.quantity ?? unit.quantity) : undefined,
        unloaded: unit.proof_of_delivery
            ? (unit.proof_of_delivery?.deviation?.quantity ??
              unit.proof_of_loading?.deviation?.quantity ??
              unit.quantity)
            : undefined,
    },
    weight: {
        target: unit.weight?.weight_kg,
        loaded: unit.proof_of_loading
            ? (unit.proof_of_loading.deviation?.weight?.weight_kg ?? unit.weight?.weight_kg)
            : undefined,
        unloaded: unit.proof_of_delivery
            ? (unit.proof_of_delivery.deviation?.weight?.weight_kg ??
              unit.proof_of_loading?.deviation?.weight?.weight_kg ??
              unit.weight?.weight_kg)
            : undefined,
    },
    loadingMeter: {
        target: unit.loading_meter?.value,
        loaded: unit.proof_of_loading
            ? (unit.proof_of_loading.deviation?.loading_meter?.value ?? unit.loading_meter?.value)
            : undefined,
        unloaded: unit.proof_of_delivery
            ? (unit.proof_of_delivery.deviation?.loading_meter?.value ??
              unit.proof_of_loading?.deviation?.loading_meter?.value ??
              unit.loading_meter?.value)
            : undefined,
    },
    length: unit.dimension?.length_cm,
    width: unit.dimension?.width_cm,
    height: unit.dimension?.height_cm,
    comments: {
        loading: unit.proof_of_loading?.deviation?.comment,
        unloading: unit.proof_of_delivery?.deviation?.comment,
    },
});

const mapArticle = (article: ApiArticle): Article => ({
    name: article.name,
    article_id: article.article_id,
    quantity: mapArticleQuantity(article.quantity),
    customer_article_id: article.customer_article_id,
});

const mapBulkMaterialUnit = (unit: ApiBulkMaterialUnit): BulkMaterialUnit => ({
    id: unit.id,
    quantity: {
        target: mapBulkMaterialQuantity(unit.quantity),
        loaded: unit.proof_of_loading
            ? mapBulkMaterialQuantity(unit.proof_of_loading.deviation?.quantity ?? unit.quantity)
            : undefined,
        unloaded: unit.proof_of_delivery
            ? mapBulkMaterialQuantity(
                  unit.proof_of_delivery.deviation?.quantity ??
                      unit.proof_of_loading?.deviation?.quantity ??
                      unit.quantity
              )
            : undefined,
    },
    material: unit.material,
    comments: {
        loading: unit.proof_of_loading?.deviation?.comment,
        unloading: unit.proof_of_delivery?.deviation?.comment,
    },
});

const mapSwapBodyUnit = (unit: ApiSwapBodyUnit): SwapBodyUnit => ({
    id: unit.id,
    swapBodyReference: {
        target: unit.id,
        loaded: unit.proof_of_loading ? (unit.proof_of_loading.swap_body_reference ?? unit.id) : undefined,
        unloaded: unit.proof_of_delivery
            ? (unit.proof_of_delivery.swap_body_reference ?? unit.proof_of_loading?.swap_body_reference ?? unit.id)
            : undefined,
    },
});

const mapCustomerTrailer = (unit: ApiCustomerTrailerUnit): CustomerTrailerUnit => ({
    id: unit.id,
    licensePlate: {
        target: unit.license_plate,
        loaded: unit.proof_of_loading ? (unit.proof_of_loading.license_plate ?? unit.license_plate) : undefined,
        unloaded: unit.proof_of_delivery
            ? (unit.proof_of_delivery.license_plate ?? unit.proof_of_loading?.license_plate ?? unit.license_plate)
            : undefined,
    },
    note: unit.note,
});
